import { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import { has, isArray } from "lodash";

import { toast } from "react-toastify";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { socketConnection } from "../../services/socket";
import { clearSession, refreshSession } from "../../services/session";
import moment from "moment";

const useAuth = () => {
  const history = useHistory();
  const [isAuth, setIsAuth] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState({});
  const [socket, setSocket] = useState(null);

  // Ref para manter referência dos listeners ativos
  const socketRef = useRef(null);

  const clearLocalSession = () => {
    clearSession();
    socketRef.current?.disconnect();
    socketRef.current = null;
    setIsAuth(false);
    setUser({});
    setSocket(null);
  };

  useEffect(() => {
    const requestInterceptor = api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem("token");
        if (token) {
          config.headers["Authorization"] = `Bearer ${JSON.parse(token)}`;
          setIsAuth(true);
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    const responseInterceptor = api.interceptors.response.use(
      (response) => {
        return response;
      },
      async (error) => {
        const originalRequest = error.config;
        const errorMessage = error?.response?.data?.error;
        const invalidSession =
          error?.response?.status === 403 &&
          typeof errorMessage === "string" &&
          errorMessage.startsWith("Invalid token.");
        if (
          invalidSession &&
          originalRequest &&
          !originalRequest._retry &&
          !originalRequest.skipAuthRefresh
        ) {
          originalRequest._retry = true;
          try {
            await refreshSession();
            originalRequest.headers = {
              ...originalRequest.headers,
              Authorization: api.defaults.headers.Authorization,
            };
            return api(originalRequest);
          } catch (refreshError) {
            if ([401, 403].includes(refreshError?.response?.status))
              clearLocalSession();
            return Promise.reject(refreshError);
          }
        }
        if (error?.response?.status === 401) {
          clearLocalSession();
        }
        return Promise.reject(error);
      }
    );
    return () => {
      api.interceptors.request.eject(requestInterceptor);
      api.interceptors.response.eject(responseInterceptor);
    };
  }, []);

  // Synchronize tenant switches across tabs sharing the refresh cookie.
  useEffect(() => {
    const changed = event => {
      if (event.key === "newzap-session-context") {
        socketRef.current?.disconnect();
        window.location.reload();
      }
    };
    window.addEventListener("storage", changed);
    return () => window.removeEventListener("storage", changed);
  }, []);

  // Effect para inicialização do token
  useEffect(() => {
    let active = true;
    const token = localStorage.getItem("token");
    (async () => {
      if (token) {
        try {
          const data = await refreshSession();
          if (active) {
            setIsAuth(true);
            setUser(data.user || data);
          }
        } catch (err) {
          if (active && [401, 403].includes(err?.response?.status))
            clearLocalSession();
          if (
            active &&
            !err.__CANCEL__ &&
            err.message !== "ERR_SESSION_CHANGED"
          )
            toastError(err);
        }
      }
      if (active) setLoading(false);
    })();
    return () => {
      active = false;
    };
  }, []);

  // Effect para configuração do socket
  useEffect(() => {
    if (!user.id || !user.companyId) return undefined;
    const socketInstance = socketConnection({
      user: { id: user.id, companyId: user.companyId },
    });
    const eventName = `company-${user.companyId}-user`;
    const handleUserUpdate = (data) => {
      if (data?.action === "update" && String(data.user?.id) === String(user.id))
        setUser(previous => ({ ...previous, ...data.user }));
    };
    socketRef.current = socketInstance;
    setSocket(socketInstance);
    socketInstance.on(eventName, handleUserUpdate);
    return () => {
      socketInstance.off(eventName, handleUserUpdate);
      if (socketRef.current === socketInstance) socketRef.current = null;
    };
  }, [user.id, user.companyId]); // Dependências específicas

  // Effect para buscar dados do usuário atual
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data.user || data);
      } catch (err) {
        console.log("Erro ao buscar usuário atual:", err);
      }
    };

    if (isAuth) {
      fetchCurrentUser();
    }
  }, [isAuth]);

  const handleLogin = async (userData) => {
    setLoading(true);

    try {
      const { data } = await api.post("/auth/login", userData);
      const {
        user: { company },
      } = data;

      // Lógica de configurações da empresa (mantém como estava)
      if (
        has(company, "companieSettings") &&
        isArray(company.companieSettings[0])
      ) {
        const setting = company.companieSettings[0].find(
          (s) => s.key === "campaignsEnabled"
        );
        if (setting && setting.value === "true") {
          localStorage.setItem("cshow", null);
        }
      }

      if (
        has(company, "companieSettings") &&
        isArray(company.companieSettings[0])
      ) {
        const setting = company.companieSettings[0].find(
          (s) => s.key === "sendSignMessage"
        );

        const signEnable = setting.value === "enable";

        if (setting && setting.value === "enabled") {
          localStorage.setItem("sendSignMessage", signEnable);
        }
      }

      localStorage.setItem("profileImage", data.user.profileImage);

      moment.locale("pt-br");
      let dueDate;
      if (data.user.company.id === 1) {
        dueDate = "2999-12-31T00:00:00.000Z";
      } else {
        dueDate = data.user.company.dueDate;
      }

      const hoje = moment(moment()).format("DD/MM/yyyy");
      const vencimento = moment(dueDate).format("DD/MM/yyyy");

      // Comparar apenas as datas (sem horas) para permitir acesso até 23h59 do dia do vencimento
      const hojeInicio = moment().startOf("day");
      const vencimentoInicio = moment(dueDate).startOf("day");

      var diff = vencimentoInicio.diff(hojeInicio, "days");
      var before = hojeInicio.isSameOrBefore(vencimentoInicio, "day");
      var dias = diff;

      if (before === true) {
        localStorage.setItem("token", JSON.stringify(data.token));
        localStorage.setItem("companyDueDate", vencimento);
        api.defaults.headers.Authorization = `Bearer ${data.token}`;
        setUser(data.user || data);
        setIsAuth(true);
        toast.success(i18n.t("auth.toasts.success"));

        if (Math.round(dias) < 5) {
          toast.warn(
            `Sua assinatura vence em ${Math.round(dias)} ${
              Math.round(dias) === 1 ? "dia" : "dias"
            } `
          );
        }

        history.push("/tickets");
        setLoading(false);
      } else {
        localStorage.setItem("token", JSON.stringify(data.token));
        api.defaults.headers.Authorization = `Bearer ${data.token}`;
        setUser(data.user || data);
        setIsAuth(true);
        toastError(`Opss! Sua assinatura venceu ${vencimento}.
Entre em contato com o Suporte para mais informações! `);
        history.push("/financeiro-aberto");
        setLoading(false);
      }
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    setLoading(true);
    const authorization = api.defaults.headers.Authorization;
    clearLocalSession();
    localStorage.removeItem("cshow");

    try {
      await api.delete("/auth/logout", {
        headers: { Authorization: authorization },
        skipAuthRefresh: true,
        timeout: 10000,
      });
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
      history.push("/login");
    }
  };

  const getCurrentUserInfo = async () => {
    try {
      const { data } = await api.get("/auth/me");
      return data;
    } catch (_) {
      return null;
    }
  };

  return {
    isAuth,
    user,
    loading,
    handleLogin,
    handleLogout,
    getCurrentUserInfo,
    socket,
    updateCompanyAfterPayment: company => setUser(previous =>
      String(previous.companyId) === String(company?.id) ? { ...previous, company: { ...previous.company, dueDate: company.dueDate, status: company.status } } : previous),
  };
};

export default useAuth;
